import { useCallback, useMemo } from 'react';
import { copy } from '../copy';
import { daysBetween, fmtDate, hasValidDateRange, plusDaysIso, todayIso } from '../lib/format';
import { clampKcal, gramsPerDay, KCAL_PER } from '../lib/nutrition';
import { PRESETS } from '../lib/plan';
import { findOverlap, nextFreeRange, PLAN_DURATION_DAYS, type OccupiedRange } from '../lib/planDates';
import { MACRO_KEYS, type GoalsPresetView, type GoalsStepModel } from '../components/steps/goalsStep.shared';
import type { MacroKey, Pct, Plan } from '../types';
import type { Dispatch } from './useDietPlanBuilder';

const samePct = (a: Pct, b: Pct) => a.p === b.p && a.c === b.c && a.f === b.f;

/**
 * Business behaviour of the "Goals" step: derived macro numbers, preset list, validation and
 * the actions that translate user input into builder-reducer actions.
 * Call once in the container; pass the result to the desktop / mobile presentation.
 */
export function useGoalsStep(plan: Plan, dispatch: Dispatch, occupied: OccupiedRange[] = []): GoalsStepModel {
  const { kcal, pct, templatePct, macroPreset } = plan;
  const templateLabel = copy.goals.presets.template;

  const presets = useMemo<GoalsPresetView[]>(() => {
    const list = templatePct ? [{ label: templateLabel, pct: templatePct }, ...PRESETS] : PRESETS;
    const activeLabel = (() => {
      if (macroPreset) return macroPreset;
      if (templatePct && samePct(pct, templatePct)) return templateLabel;
      return PRESETS.find(pr => samePct(pct, pr.pct))?.label ?? null;
    })();
    return list.map(pr => ({
      label: pr.label,
      chipLabel:
        pr.label === templateLabel ? pr.label : `${pr.label} ${pr.pct.p}/${pr.pct.c}/${pr.pct.f}`,
      pct: pr.pct,
      active: pr.label === activeLabel,
    }));
  }, [templatePct, pct, macroPreset, templateLabel]);

  const sumPct = pct.p + pct.c + pct.f;

  // When the split drifts off 100, suggest spreading the gap across all macros in proportion to their share.
  const suggestion = useMemo(() => {
    if (sumPct === 100) return null;
    const delta = 100 - sumPct;
    if (sumPct <= 0) return null;
    const exact = MACRO_KEYS.map(k => ({ k, v: pct[k] + (delta * pct[k]) / sumPct }));
    const next = Object.fromEntries(exact.map(x => [x.k, Math.floor(x.v)])) as Pct;
    let left = 100 - (next.p + next.c + next.f);
    [...exact].sort((x, y) => (y.v % 1) - (x.v % 1)).forEach(x => {
      if (left > 0) { next[x.k] += 1; left -= 1; }
    });
    return { delta, pct: next };
  }, [pct, sumPct]);

  const grams = useMemo(
    () => Object.fromEntries(MACRO_KEYS.map(k => [k, gramsPerDay(kcal, pct, k)])) as Record<MacroKey, number>,
    [kcal, pct],
  );
  const macroKcal = useMemo(
    () => Object.fromEntries(MACRO_KEYS.map(k => [k, Math.round((kcal * pct[k]) / 100)])) as Record<MacroKey, number>,
    [kcal, pct],
  );

  const patch = useCallback((fn: (p: Plan) => void) => dispatch({ type: 'plan', fn }), [dispatch]);

  const datesOk = hasValidDateRange(plan.from, plan.to);
  const overlap = useMemo(
    () => (datesOk ? findOverlap(plan.from, plan.to, occupied) : null),
    [datesOk, plan.from, plan.to, occupied],
  );
  const suggestedRange = useMemo(
    () => nextFreeRange(occupied, todayIso(), PLAN_DURATION_DAYS, plusDaysIso),
    [occupied],
  );
  const applySuggestedDates = useCallback(() => {
    patch((p) => {
      p.from = suggestedRange.from;
      p.to = suggestedRange.to;
    });
  }, [patch, suggestedRange.from, suggestedRange.to]);

  const setName = useCallback((v: string) => patch(p => { p.name = v; }), [patch]);
  const setFrom = useCallback((v: string) => patch(p => { p.from = v; }), [patch]);
  const setTo = useCallback((v: string) => patch(p => { p.to = v; }), [patch]);
  const setKcal = useCallback((v: number) => patch(p => { p.kcal = clampKcal(Number(v) || 0); }), [patch]);
  const stepKcal = useCallback((delta: number) => dispatch({ type: 'kcal', delta }), [dispatch]);
  const setPct = useCallback((key: MacroKey, value: number) => {
    dispatch({ type: 'pct', key, value });
  }, [dispatch]);
  const applySuggestion = useCallback(() => {
    if (!suggestion) return;
    patch(p => { p.pct = { ...suggestion.pct }; p.macroPreset = null; });
  }, [patch, suggestion]);
  const applyPreset = useCallback((next: Pct, label: string) => dispatch({ type: 'preset', pct: next, label }), [dispatch]);
  const stepPct = useCallback((key: MacroKey, delta: number) => {
    dispatch({ type: 'pct', key, value: pct[key] + delta });
  }, [dispatch, pct]);
  const openSplit = useCallback(() => dispatch({ type: 'sheet', sheet: 'split' }), [dispatch]);
  const setGrams = useCallback(
    (key: MacroKey, g: number) => {
      if (!kcal) return;
        dispatch({ type: 'pct', key, value: ((g * KCAL_PER[key]) / kcal) * 100 });
    },
    [dispatch, kcal],
  );

  return {
    name: plan.name,
    from: plan.from,
    to: plan.to,
    kcal,
    pct,
    presets,
    sumPct,
    isBalanced: sumPct === 100,
    suggestion,
    applySuggestion,
    datesOk,
    overlap,
    suggestedRange,
    applySuggestedDates,
    grams,
    macroKcal,
    dateRangeLabel: hasValidDateRange(plan.from, plan.to)
      ? `${daysBetween(plan.from, plan.to)} days · ${fmtDate(plan.from)} to ${fmtDate(plan.to)}`
      : '',
    templateHint: plan.templateName ? `Template: ${plan.templateName}` : null,
    splitSummary: `${plan.split === 'template' ? 'Template split' : 'Standard split'} · ${plan.meals.filter(m => !m.skip).length} meals`,
    setName,
    setFrom,
    setTo,
    setKcal,
    stepKcal,
    setPct,
    setGrams,
    stepPct,
    applyPreset,
    openSplit,
  };
}
